import React from "react";
import { FaArrowDown, FaArrowUp, FaEdit, FaTrash } from "react-icons/fa";
import { formatPascal, formatPhone } from "../utils/formatters";
import NotesPreviewCell from "../components/NotesPreviewCell";
import "../assets/css/Projects.css";
import "../assets/css/Accounts.css";

function SortableHeader({
  columnId,
  label,
  sortColumn,
  sortDirection,
  onSort,
}) {
  const active = sortColumn === columnId;
  return (
    <button
      type="button"
      className="accounts-sort-header"
      onClick={() => onSort(columnId)}
      aria-label={`Sort by ${label}${active ? `, ${sortDirection === "asc" ? "ascending" : "descending"}` : ""}`}
    >
      {label}
      <span className="accounts-sort-arrows" aria-hidden>
        <FaArrowUp
          className={
            active && sortDirection === "asc"
              ? "accounts-sort-arrow accounts-sort-arrow--active"
              : "accounts-sort-arrow"
          }
        />
        <FaArrowDown
          className={
            active && sortDirection === "desc"
              ? "accounts-sort-arrow accounts-sort-arrow--active"
              : "accounts-sort-arrow"
          }
        />
      </span>
    </button>
  );
}

/**
 * Accounts table used on the Accounts tab inside DigixLeads.
 */
function AccountsTable({
  rows,
  sortColumn = null,
  sortDirection = "asc",
  onSort,
  getColumnWidth,
  getColumnMinWidth,
  handleResizeStart,
  handleView,
  handleEdit,
  handleDelete,
}) {
  const sortProps =
    typeof onSort === "function"
      ? { sortColumn, sortDirection, onSort }
      : null;

  return (
    <table className="data-table accounts-table-frozen">
      <thead>
        <tr>
          <th
            className="col-sticky col-0 accounts-th-sortable"
            style={{ minWidth: getColumnMinWidth(0) || "120px", width: getColumnWidth(0), position: "relative" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              {sortProps ? (
                <SortableHeader columnId="accountName" label="Account Name" {...sortProps} />
              ) : (
                <span>Account Name</span>
              )}
              <span
                className="column-resizer"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  if (handleResizeStart) {
                    handleResizeStart(e, 0);
                  }
                }}
              ></span>
            </div>
          </th>
          <th
            className="accounts-th-sortable"
            style={{ minWidth: getColumnMinWidth(1) || "120px", width: getColumnWidth(1), position: "relative" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              {sortProps ? (
                <SortableHeader columnId="username" label="Username" {...sortProps} />
              ) : (
                <span>Username</span>
              )}
              <span
                className="column-resizer"
                onMouseDown={(e) => handleResizeStart && handleResizeStart(e, 1)}
              ></span>
            </div>
          </th>
          <th
            className="accounts-th-sortable"
            style={{ minWidth: getColumnMinWidth(2) || "150px", width: getColumnWidth(2), position: "relative" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              {sortProps ? (
                <SortableHeader columnId="url" label="URL" {...sortProps} />
              ) : (
                <span>URL</span>
              )}
              <span
                className="column-resizer"
                onMouseDown={(e) => handleResizeStart && handleResizeStart(e, 2)}
              ></span>
            </div>
          </th>
          <th
            className="accounts-th-sortable"
            style={{ minWidth: getColumnMinWidth(3) || "120px", width: getColumnWidth(3), position: "relative" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              {sortProps ? (
                <SortableHeader columnId="email" label="Email" {...sortProps} />
              ) : (
                <span>Email</span>
              )}
              <span
                className="column-resizer"
                onMouseDown={(e) => handleResizeStart && handleResizeStart(e, 3)}
              ></span>
            </div>
          </th>
          <th
            className="accounts-th-sortable"
            style={{ minWidth: getColumnMinWidth(4) || "120px", width: getColumnWidth(4), position: "relative" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              {sortProps ? (
                <SortableHeader columnId="contact" label="Contact Number" {...sortProps} />
              ) : (
                <span>Contact Number</span>
              )}
              <span
                className="column-resizer"
                onMouseDown={(e) => handleResizeStart && handleResizeStart(e, 4)}
              ></span>
            </div>
          </th>
          <th
            className="accounts-th-sortable"
            style={{ minWidth: getColumnMinWidth(5) || "180px", width: getColumnWidth(5), position: "relative" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              {sortProps ? (
                <SortableHeader columnId="notes" label="Notes" {...sortProps} />
              ) : (
                <span>Notes</span>
              )}
              <span
                className="column-resizer"
                onMouseDown={(e) => handleResizeStart && handleResizeStart(e, 5)}
              ></span>
            </div>
          </th>
          <th
            className="accounts-th-sortable"
            style={{ minWidth: getColumnMinWidth(6) || "120px", width: getColumnWidth(6), position: "relative" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              {sortProps ? (
                <SortableHeader columnId="platform" label="Platform" {...sortProps} />
              ) : (
                <span>Platform</span>
              )}
              <span
                className="column-resizer"
                onMouseDown={(e) => handleResizeStart && handleResizeStart(e, 6)}
              ></span>
            </div>
          </th>
          <th
            className="col-sticky col-action"
            style={{ minWidth: getColumnMinWidth(7) || "120px", width: getColumnWidth(7), position: "relative" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>Action</div>
            <span
              className="column-resizer"
              onMouseDown={(e) => handleResizeStart && handleResizeStart(e, 7)}
            ></span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan="8" style={{ textAlign: "center", padding: "40px", color: "#999" }}>
              No accounts found.
            </td>
          </tr>
        ) : (
          rows.map((account) => (
            <tr key={account.id || account.account_id}>
              <td
                className="col-sticky col-0"
                style={{ minWidth: getColumnMinWidth(0) || "120px", width: getColumnWidth(0) }}
              >
                <span
                  className="table-name-link"
                  onClick={() => handleView(account.id || account.account_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleView(account.id || account.account_id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {formatPascal(account.accountName || account.account_name) || (
                    <span className="muted-text">—</span>
                  )}
                </span>
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(1) || "120px", width: getColumnWidth(1) }}
              >
                {account.username || "—"}
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(2) || "150px", width: getColumnWidth(2) }}
              >
                {account.profileUrl || account.profile_url ? (
                  <a
                    href={account.profileUrl || account.profile_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#2977ed", textDecoration: "none" }}
                  >
                    {account.profileUrl || account.profile_url}
                  </a>
                ) : (
                  "—"
                )}
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(3) || "120px", width: getColumnWidth(3) }}
              >
                {account.emailUsed || account.email_used || "—"}
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(4) || "120px", width: getColumnWidth(4) }}
              >
                {formatPhone(
                  account.contact ||
                    account.contact_name ||
                    account.contactPhone ||
                    account.contact_phone
                ) || "—"}
              </td>
              <td
                className="accounts-notes-td"
                style={{ minWidth: getColumnMinWidth(5) || "180px", width: getColumnWidth(5) }}
              >
                <NotesPreviewCell notes={account.notes} title="Notes" />
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(6) || "120px", width: getColumnWidth(6) }}
              >
                {formatPascal(account.platform) || "—"}
              </td>
              <td
                className="col-sticky col-action"
                style={{ minWidth: getColumnMinWidth(7) || "120px", width: getColumnWidth(7) }}
              >
                <div className="project-actions-cell">
                  {typeof handleEdit === "function" && (
                    <button
                      type="button"
                      className="project-action-btn project-action-btn-icon project-action-btn-edit"
                      onClick={() => handleEdit(account.id || account.account_id)}
                      title="Edit Account"
                    >
                      <FaEdit aria-hidden />
                    </button>
                  )}
                  {typeof handleDelete === "function" && (
                    <button
                      type="button"
                      className="project-action-btn project-action-btn-icon project-action-btn-deactivate"
                      onClick={() => handleDelete(account.id || account.account_id)}
                      title="Delete Account"
                    >
                      <FaTrash aria-hidden />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

export default AccountsTable;
